import type { SettingsRegistryEntry } from "@magic-md/engine-client/config";
import { isPlainObject } from "./format-utils";

export function isDirectoryStringSetting(entry: SettingsRegistryEntry): boolean {
    const rawType = Array.isArray(entry.schema.type)
        ? entry.schema.type[0]
        : entry.schema.type;
    return rawType === "string" && directoryLikeKey(entry.key);
}

export function isDirectoryArraySetting(entry: SettingsRegistryEntry): boolean {
    const rawType = Array.isArray(entry.schema.type)
        ? entry.schema.type[0]
        : entry.schema.type;
    if (rawType !== "array") return false;
    const items = entry.schema.items;
    const itemType =
        isPlainObject(items) && typeof (items as Record<string, unknown>).type === "string"
            ? (items as Record<string, unknown>).type as string
            : null;
    return itemType === "string" && directoryLikeKey(entry.key);
}

export function directoryLikeKey(key: string): boolean {
    return (
        key.endsWith("Dir") ||
        key.endsWith("Dirs") ||
        key.endsWith("Directory") ||
        key.endsWith("Directories")
    );
}

export function hasSchemaProperties(schema: Record<string, unknown>): boolean {
    return getSchemaProperties(schema).length > 0;
}

export function getSchemaProperties(
    schema: Record<string, unknown>,
): Array<[string, Record<string, unknown>]> {
    const properties = schema.properties;
    if (!isPlainObject(properties)) {
        return [];
    }
    return Object.entries(properties as Record<string, unknown>).filter((entry): entry is [string, Record<string, unknown>] =>
        isPlainObject(entry[1]),
    );
}

export function schemaPrimaryType(schema: Record<string, unknown>): string | null {
    const rawType = schema.type;
    if (Array.isArray(rawType)) {
        const normalized = rawType.filter(
            (item): item is string => typeof item === "string" && item !== "null",
        );
        return normalized[0] ?? null;
    }
    return typeof rawType === "string" ? rawType : null;
}

export function schemaItemsPrimaryType(schema: Record<string, unknown>): string | null {
    const items = schema.items;
    if (!isPlainObject(items)) return null;
    return schemaPrimaryType(items as Record<string, unknown>);
}

export function schemaTypeLabel(schema: Record<string, unknown>): string {
    if (Array.isArray(schema.enum) && schema.enum.length > 0) {
        return "enum";
    }
    const rawType = schemaPrimaryType(schema);
    if (rawType === "array") {
        const itemType = schemaItemsPrimaryType(schema);
        return itemType ? `${itemType}[]` : "array";
    }
    return rawType ?? "value";
}

export function updateObjectField(
    value: Record<string, unknown>,
    fieldName: string,
    nextValue: unknown,
): Record<string, unknown> {
    const next = { ...value };
    if (nextValue === undefined) {
        delete next[fieldName];
    } else {
        next[fieldName] = nextValue;
    }
    return next;
}

export function removeObjectField(
    value: Record<string, unknown>,
    fieldName: string,
): Record<string, unknown> {
    const next = { ...value };
    delete next[fieldName];
    return next;
}
